import * as RadixPopover from '@radix-ui/react-popover';

import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { DropdownMenu } from '../DropdownMenu';
import { MenuHighlight } from '../DropdownMenu/MenuHighlight';
import type { DropdownContentProps } from './Dropdown.types';
import { useDropdownContext } from './DropdownContext';

/**
 * Compound Dropdown content — **web** implementation.
 *
 * Portals a Radix `Popover.Content` holding the shared {@link DropdownMenu}
 * glass panel. The panel is a flex column: `topSlot`, a scrollable item list,
 * `bottomSlot` — the slots stay pinned while a long list scrolls within the
 * room Radix reports between the trigger and the viewport edge.
 *
 * The native counterpart at `DropdownContent.native.tsx` mounts a bottom sheet.
 */
/** Mirrors `.flashka-popup-content`'s `--popup-anim-duration` fallback. */
const DEFAULT_ANIMATION_MS = 180;

export function DropdownContent({
  children,
  topSlot,
  bottomSlot,
  // Default to `bottom` — Radix still flips to `top` on collision, so this
  // only sets the *preferred* side.
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
  minWidth = 240,
  matchTriggerWidth = false,
  className,
  onCloseAutoFocus,
  hideWhenDetached = false,
  animationDuration = 180,
  animationBezier,
}: DropdownContentProps) {
  const { size, testID, anchorRef } = useDropdownContext('DropdownContent');

  return (
    <RadixPopover.Portal>
      <RadixPopover.Content
        side={side === 'top' ? 'top' : 'bottom'}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={12}
        // Opt-in — see the prop's doc comment for why it can't be the default.
        hideWhenDetached={hideWhenDetached}
        // Suppress the outside-dismiss when the interaction starts ON the
        // trigger — the trigger's own `onPress` toggle owns open/close, so
        // without this a click-to-close would immediately re-open. Also
        // suppress it for submenu flyouts: they portal to `body`, i.e.
        // outside this content's DOM subtree, but they're logically inside
        // the menu. Genuinely outside interactions still dismiss.
        onInteractOutside={(e) => {
          const target = e.detail.originalEvent.target as Node | null;
          if (!target) return;
          if (anchorRef?.current?.contains(target)) e.preventDefault();
          else if (
            target instanceof Element &&
            target.closest('[data-flashka-dropdown-sub]')
          )
            e.preventDefault();
        }}
        // Web passthrough — a call site can `preventDefault()` to stop focus
        // returning to the trigger (e.g. an item opens an autofocusing popover).
        onCloseAutoFocus={onCloseAutoFocus}
        // Same reason the trigger stops its click (see DropdownTrigger), for
        // the other half of the menu. This panel is portaled to `body`, but
        // React synthetic events bubble the REACT tree, not the DOM one — so an
        // item click still reaches whatever the <Dropdown> is nested in and
        // fires it. Selecting "Rename" would open the dialog AND navigate.
        // Stopping here is safe: the item's own onSelect has already run by the
        // time the event bubbles this far.
        onClick={(event) => event.stopPropagation()}
        // Open/close morph — the shared `.flashka-popup-content` rule
        // (flashka-web/src/index.css) owns the keyframes and the default
        // duration + curve; the two `--popup-anim-*` custom properties are set
        // here ONLY when this call site overrides them, so the DS default
        // stays a single source of truth.
        className={animationDuration > 0 ? 'flashka-popup-content' : undefined}
        style={{
          // Radix sets the anchor's width on the content as a variable — the
          // anchor is the trigger's wrapper, so a full-width trigger's menu
          // is the button's own width, and follows it.
          ...(matchTriggerWidth
            ? { width: 'var(--radix-popover-trigger-width)' }
            : { minWidth }),
          zIndex: 1000,
          transformOrigin: 'var(--radix-popover-content-transform-origin)',
          ...(animationDuration > 0 && animationDuration !== DEFAULT_ANIMATION_MS
            ? { ['--popup-anim-duration' as never]: `${animationDuration}ms` }
            : {}),
          ...(animationDuration > 0 && animationBezier
            ? {
                ['--popup-anim-timing' as never]: `cubic-bezier(${clampX(animationBezier[0])}, ${animationBezier[1]}, ${clampX(animationBezier[2])}, ${animationBezier[3]})`,
              }
            : {}),
        }}>
        <DropdownMenu
          size={size}
          // Cap the panel to the room Radix reports; the inner list scrolls.
          className={cn(
            'web:max-h-[var(--radix-popover-content-available-height)]',
            className,
          )}
          testID={testID ? `${testID}-popup` : undefined}>
          {topSlot}
          <View
            // `p-0.5 -m-0.5`: a scroll container clips at its padding box, and
            // the rows fill it edge to edge — so a focused row's 1px ring (drawn
            // OUTSIDE its border box) was being shaved off on all four sides. The
            // padding gives the ring room inside the clip; the equal negative
            // margin keeps the margin box — and so the panel's gaps — untouched.
            // `shrink` pairs with `min-h-0`: this scroller is a flex child of a
            // panel capped at the available height, and the web View primitive
            // defaults to NO shrink (RN parity — see View.tsx). Without it the
            // list keeps its full content height and spills out of the panel
            // instead of scrolling inside it.
            className="-m-0.5 flex min-h-0 shrink flex-col overflow-y-auto overflow-x-hidden p-0.5"
            webProps={{ onWheel: onWheelGuard }}>
            {/* Inside the scroller, so the shared hover indicator scrolls with
                the rows it tracks. */}
            <MenuHighlight className="flex flex-col gap-1">{children}</MenuHighlight>
          </View>
          {bottomSlot}
        </DropdownMenu>
      </RadixPopover.Content>
    </RadixPopover.Portal>
  );
}

/**
 * Stop a wheel event from bubbling out of the popup's scroll container.
 *
 * When the menu lives inside a scroll-locked modal (Radix Dialog wraps content
 * in `react-remove-scroll`), that lock attaches a document-level non-passive
 * `wheel` listener that `preventDefault`s events it considers "outside" — which
 * silently kills wheel/trackpad scrolling inside this portaled popup. Stopping
 * propagation here (before the event reaches that listener) lets the browser
 * scroll the list while the page behind stays locked. No `preventDefault`, so
 * the page never scrolls. No-op outside a modal.
 */
function onWheelGuard(e: { stopPropagation: () => void }): void {
  e.stopPropagation();
}

/**
 * Clamp a CSS cubic-bezier x-component to `[0, 1]` per spec — the animation
 * playground slider lets reviewers drag x outside `[0, 1]` for the curve
 * preview, but the generated `cubic-bezier(…)` must stay in spec.
 */
function clampX(v: number): number {
  return Math.min(1, Math.max(0, v));
}
